import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { cn } from "@lib/utils/helpers";
import { useAppSelector } from "@/app/hooks";
import {
  selectApplicationData,
  selectHasMultipleActiveApplications,
  selectHasSelectedApplication,
  selectIsReadOnly,
} from "@/features/applicant/applicationSlice";
import { selectUser } from "@/features/auth/authSlice";
import { logoutAndRedirect } from "@/lib/api/authService";
import {
  canAccessTab,
  getTabPathWithApplicantId,
  getTabNavigationOptions,
  getCurrentTabFromLocation,
  APPLICATION_TABS,
  type ApplicationTab,
} from "@lib/utils/applicationNavigation";
import { calculateRentAdded } from "@lib/utils/applicationHeaderHelpers";
import { ValidationModal } from "@components/ui/ValidationModal";
import { useTranslation } from "@lib/i18n/useTranslation";
import { useTabValidation } from "@/features/applicant/context/TabValidationContext";
import {
  Home,
  Mail,
  FileText,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  User,
  Menu,
  Handshake,
  X,
  LogOut,
  Loader2,
} from "lucide-react";
import { SidebarLogo } from "@/components/layout/SidebarLogo";
import { APPLICANT_CONTENT } from "@/features/applicant/constants/applicantContent";
import type { NavItem } from "./ApplicantSidebar.types";

export function ApplicantSidebar() {
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [isProfileExpanded, setIsProfileExpanded] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { validate } = useTabValidation();
  const location = useLocation();
  const navigate = useNavigate();
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>();

  // Get application data from Redux
  const applicationData = useAppSelector(selectApplicationData);
  const authUser = useAppSelector(selectUser);
  const isReadOnly = useAppSelector(selectIsReadOnly);
  const hasMultipleActiveApplications = useAppSelector(
    selectHasMultipleActiveApplications,
  );
  const hasSelectedApplication = useAppSelector(selectHasSelectedApplication);

  // Hide Current Application menu when multiple active applications exist and user hasn't selected one
  // This matches Angular behavior where the sidebar tabs are hidden until user selects an application
  const hideCurrentApplication =
    Boolean(hasMultipleActiveApplications && !hasSelectedApplication);

  // Get applicant ID from URL or Redux
  const applicantId = urlApplicantId || applicationData?.applicant_id;

  // Check if payment is added (allows direct navigation if true)
  const isPaymentAdded = calculateRentAdded(applicationData);

  // Handle logout confirmation
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // Handle logout confirmation - Yes
  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    // Logout (backend API + clear storage) and redirect to login.
    // Do NOT dispatch logout() here — clearing Redux auth state before redirect
    // causes ProtectedRoute to detect unauthenticated state and redirect to dashboard.
    // logoutAndRedirect() handles all cleanup and redirects directly to the login page.
    await logoutAndRedirect();
  };

  // Handle logout cancellation - No
  const handleLogoutCancel = () => {
    if (isLoggingOut) return;
    setShowLogoutModal(false);
  };

  // Handle sub-menu item navigation with validation
  const handleSubItemClick = (
    e: React.MouseEvent,
    path: string,
    targetTab: ApplicationTab,
  ) => {
    e.preventDefault();

    const url = new URL(path, window.location.origin);
    const targetTabIndex = APPLICATION_TABS.indexOf(targetTab);
    const currentTab = getCurrentTabFromLocation(location);
    const currentTabIndex = APPLICATION_TABS.indexOf(currentTab);
    const isCurrentPage =
      location.pathname === url.pathname && currentTab === targetTab;

    if (isCurrentPage) {
      return;
    }

    // Allow going backwards without validating; only validate when moving forward and not read-only.
    // In read-only mode (e.g. after application fee paid), allow free navigation via sidebar.
    const isGoingBack = targetTabIndex < currentTabIndex;
    if (!isReadOnly && !isGoingBack) {
      const validation = validate();
      if (!validation.isValid) {
        return; // Navigation blocked by validate() which also shows the modal
      }
    }

    // Check if target tab can be accessed (e.g. previous steps complete when going forward)
    const accessCheck = canAccessTab(
      targetTabIndex,
      applicationData,
      isReadOnly,
      isPaymentAdded,
      true,
    );

    if (!accessCheck.canAccess) {
      const reason =
        accessCheck.reason || t("errors.navigation.completePreviousSteps");
      setValidationMessage(reason);
      setShowValidationModal(true);
      return;
    }

    const isNewRouteFormat = url.pathname.match(
      /^\/resident-application\/\d+$/,
    );
    if (isNewRouteFormat && applicantId) {
      navigate(path, getTabNavigationOptions(targetTab));
    } else {
      navigate(path);
    }
  };

  // Dynamic application ID
  const applicationId =
    applicationData?.fk_application_id ||
    applicationData?.application_id ||
    "-";
  const applicationMenuName =
    applicationId !== "-"
      ? `Current Application - ${applicationId} `
      : "Current Application -";

  // User info - memoized to prevent unnecessary recalculations
  // Priority: applicationData > authUser > 'User'
  const { userInitials, fullName } = useMemo(() => {
    const firstName =
      applicationData?.first_name ||
      applicationData?.applicant_first_name ||
      authUser?.first_name ||
      "";
    const lastName =
      applicationData?.last_name ||
      applicationData?.applicant_last_name ||
      authUser?.last_name ||
      "";

    // Generate initials safely
    let initials = "U";
    if (firstName && lastName) {
      initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
      initials = firstName.charAt(0).toUpperCase();
    } else if (lastName) {
      initials = lastName.charAt(0).toUpperCase();
    }

    const name = `${firstName} ${lastName}`.trim() || "User";

    return {
      userInitials: initials,
      fullName: name,
    };
  }, [
    applicationData?.first_name,
    applicationData?.applicant_first_name,
    applicationData?.last_name,
    applicationData?.applicant_last_name,
    authUser?.first_name,
    authUser?.last_name,
  ]);

  const [expandedItems, setExpandedItems] = useState<string[]>([
    applicationMenuName,
  ]);

  // Update expanded items when application ID changes
  useEffect(() => {
    setExpandedItems([applicationMenuName]);
  }, [applicationMenuName]);

  // Memoize navigation to prevent recreation on every render
  // Use resident-application routes if applicant ID is available
  // Hide Current Application menu when multiple active applications exist (matching Angular behavior)
  const navigation: NavItem[] = useMemo(() => {
    const baseAboutPath = applicantId
      ? getTabPathWithApplicantId("about", applicantId)
      : "/applicant/application/about";
    const baseAddressPath = applicantId
      ? getTabPathWithApplicantId("address", applicantId)
      : "/applicant/application/address";
    const basePropertyPath = applicantId
      ? getTabPathWithApplicantId("property", applicantId)
      : "/applicant/application/property";
    const baseIncomePath = applicantId
      ? getTabPathWithApplicantId("income", applicantId)
      : "/applicant/application/income";
    const baseHistoryPath = applicantId
      ? getTabPathWithApplicantId("history", applicantId)
      : "/applicant/application/history";
    const baseSignaturePath = applicantId
      ? getTabPathWithApplicantId("signature", applicantId)
      : "/applicant/application/signature";
    const basePaymentPath = applicantId
      ? getTabPathWithApplicantId("payment", applicantId)
      : "/applicant/application/payment";

    const navItems: NavItem[] = [
      { name: "Home", path: "/applicant/dashboard", icon: Home },
      { name: "Message", path: "/applicant/messages", icon: Mail },
    ];

    // Only show Current Application menu when user has selected an application
    // or when there's only one active application (hideCurrentApplication is false)
    if (!hideCurrentApplication) {
      navItems.push({
        name: applicationMenuName,
        path: baseAboutPath,
        icon: FileText,
        subItems: [
          { name: "About Yourself", path: baseAboutPath },
          { name: "Current Living Address", path: baseAddressPath },
          { name: "Property", path: basePropertyPath },
          { name: "Income and Employment", path: baseIncomePath },
          { name: "Personal History", path: baseHistoryPath },
          { name: "Acknowledgements and Signature", path: baseSignaturePath },
          { name: "Payment Log", path: basePaymentPath },
        ],
      });
    }

    navItems.push({
      name: "Help / About",
      path: "/applicant/help",
      icon: Handshake,
    });

    return navItems;
  }, [applicationMenuName, applicantId, hideCurrentApplication]);

  // Detect mobile/tablet viewport (<1024px) - hamburger visible on phones, tablets, small desktops
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsExpanded(true);
      document.body.style.overflow = "hidden";
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsExpanded(false);
      document.body.style.overflow = "auto";
    }
  };

  const toggleSubMenu = (itemName: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemName)
        ? prev.filter((name) => name !== itemName)
        : [...prev, itemName],
    );
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  // Close sidebar on route change if mobile
  useEffect(() => {
    if (isMobile) setIsExpanded(false);
  }, [location.pathname, isMobile]);

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobile && isExpanded && (
        <div
          className="fixed inset-0 bg-black/50 z-[49]"
          onClick={() => setIsExpanded(false)}
        />
      )}

      {/* Floating Hamburger (only when sidebar is off-screen on mobile) */}
      {isMobile && !isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="fixed top-3 left-4 z-[48] p-2 bg-white rounded-md shadow-md text-gray-700"
          aria-label="Open menu"
        >
          <Menu className="h-6 w-6" />
        </button>
      )}

      <aside
        className={cn(
          "fixed left-0 top-0 h-screen bg-[#3B4D66] text-white transition-all duration-300 ease-in-out z-50 shadow-xl flex flex-col",
          isMobile
            ? isExpanded
              ? "w-64 translate-x-0"
              : "w-64 -translate-x-full"
            : isExpanded
              ? "w-64"
              : "w-16",
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Burger menu row - inside sidebar, above logo (visible on all screen sizes) */}
        <div className="flex items-center flex-shrink-0 border-b border-gray-700/50 bg-[#3B4D66]">
          {isExpanded ? (
            <button
              onClick={() => setIsExpanded(false)}
              className="flex items-center justify-center w-16 h-12 text-white hover:bg-white/10 transition-colors"
              aria-label="Close menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          ) : (
            <button
              onClick={() => setIsExpanded(true)}
              className="flex items-center justify-center w-full h-12 text-white hover:bg-white/10 transition-colors"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          )}
        </div>

        <SidebarLogo
          isExpanded={isExpanded}
          orgLogo={authUser?.current_role?.org_logo}
          orgSwooshLogo={authUser?.current_role?.org_swoosh_logo}
          orgWebsite={authUser?.current_role?.org_website}
          alt={
            authUser?.current_role?.org_name ||
            APPLICANT_CONTENT.login.logo.title
          }
          className="h-12 min-h-12 max-h-12"
          trailing={
            isExpanded && isMobile ? (
              <button
                data-testid="applicant-sidebar-logo-close-menu-button"
                onClick={() => setIsExpanded(false)}
                className="ml-auto p-2 -mr-2 flex-shrink-0 text-gray-500 transition-colors hover:text-gray-800"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            ) : null
          }
        />

        {/* User Profile Section */}
        <div className="px-4 py-4 border-b border-gray-700/50">
          {isExpanded ? (
            <div className="transition-opacity duration-300">
              <button
                onClick={() => setIsProfileExpanded(!isProfileExpanded)}
                className="w-full flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-600 text-sm font-bold text-white">
                    {userInitials}
                  </div>
                  <span className="text-sm font-semibold text-white">
                    {fullName}
                  </span>
                </div>
                {isProfileExpanded ? (
                  <ChevronUp className="h-4 w-4 text-white" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-white" />
                )}
              </button>
              {isProfileExpanded && (
                <div className="mt-3 ml-12 space-y-2">
                  <Link
                    to="/applicant/account"
                    className="block text-sm text-gray-300 hover:text-white transition-colors"
                  >
                    • Account
                  </Link>
                  <button
                    onClick={handleLogoutClick}
                    className="block text-sm text-gray-300 hover:text-white transition-colors text-left w-full"
                  >
                    • Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-600 text-xs font-bold text-white">
                {userInitials}
              </div>
            </div>
          )}
        </div>

        {/* Role Section */}
        <div className="px-4 py-3 border-b border-gray-700/50">
          {isExpanded ? (
            <div className="transition-opacity duration-300">
              <p className="text-xs text-gray-400 mb-2">Role</p>
              <button className="w-full flex items-center justify-between text-white hover:text-teal-400 transition-colors">
                <span className="text-sm font-medium">Resident</span>
                <ChevronRight className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          ) : (
            <div className="flex justify-center">
              <User className="h-5 w-5 text-gray-300" />
            </div>
          )}
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent hover:scrollbar-thumb-gray-500 min-h-0">
          {navigation.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.subItems &&
                item.subItems.some((sub) => location.pathname === sub.path));
            const Icon = item.icon;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isSubMenuExpanded = expandedItems.includes(item.name);

            return (
              <div key={item.name}>
                {hasSubItems ? (
                  <button
                    onClick={() => toggleSubMenu(item.name)}
                    className={cn(
                      "w-full flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 group relative",
                      isActive
                        ? "bg-[#4A5F7F] text-white shadow-md"
                        : "text-gray-300 hover:bg-[#4A5F7F]/80 hover:text-white hover:shadow-sm",
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-teal-500 rounded-r-full" />
                    )}
                    <Icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0 transition-all duration-200",
                        isActive
                          ? "text-teal-400"
                          : "text-gray-400 group-hover:text-white group-hover:scale-110",
                      )}
                    />
                    {isExpanded && (
                      <>
                        <span className="ml-3 flex-1 text-left transition-opacity duration-300 opacity-100">
                          {item.name}
                        </span>
                        {isSubMenuExpanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        )}
                      </>
                    )}
                  </button>
                ) : (
                  <Link
                    to={item.path}
                    className={cn(
                      "flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-all duration-200 group relative",
                      isActive
                        ? "bg-[#4A5F7F] text-white shadow-md"
                        : "text-gray-300 hover:bg-[#4A5F7F]/80 hover:text-white hover:shadow-sm",
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-teal-500 rounded-r-full" />
                    )}
                    <Icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0 transition-all duration-200",
                        isActive
                          ? "text-teal-400"
                          : "text-gray-400 group-hover:text-white group-hover:scale-110",
                      )}
                    />
                    {isExpanded && (
                      <span className="ml-3 transition-opacity duration-300 opacity-100">
                        {item.name}
                      </span>
                    )}
                  </Link>
                )}

                {/* Sub-menu items */}
                {hasSubItems && isExpanded && isSubMenuExpanded && (
                  <div className="mt-1 ml-4 space-y-1">
                    {item.subItems!.map((subItem, subItemIndex) => {
                      // Check if sub-item is active by comparing pathname and tab state
                      const subItemUrl = new URL(
                        subItem.path,
                        window.location.origin,
                      );
                      const currentTab = getCurrentTabFromLocation(location);

                      // Tab for this sub-item: by index when path has no ?tab= (resident-application), else from path
                      const isNewRouteFormat = subItemUrl.pathname.match(
                        /^\/resident-application\/\d+$/,
                      );
                      let subItemTab: ApplicationTab = "about";
                      if (isNewRouteFormat) {
                        subItemTab = APPLICATION_TABS[subItemIndex] ?? "about";
                      } else {
                        const tabFromPath = subItemUrl.pathname.split("/").pop();
                        if (
                          tabFromPath &&
                          APPLICATION_TABS.includes(tabFromPath as ApplicationTab)
                        ) {
                          subItemTab = tabFromPath as ApplicationTab;
                        }
                      }

                      const isSubActive =
                        location.pathname === subItemUrl.pathname &&
                        currentTab === subItemTab;

                      return (
                        <button
                          key={subItem.path}
                          onClick={(e) =>
                            handleSubItemClick(e, subItem.path, subItemTab)
                          }
                          className={cn(
                            "w-full flex items-center px-3 py-2 text-xs rounded-lg transition-all duration-200 text-left",
                            isSubActive
                              ? "bg-[#4A5F7F] text-white font-medium"
                              : "text-gray-400 hover:bg-[#4A5F7F]/60 hover:text-white cursor-pointer",
                          )}
                        >
                          <span className="ml-2">{subItem.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout Confirmation Modal - portaled to body so it centers on screen, not sidebar */}
        {showLogoutModal &&
          createPortal(
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4 sm:p-6">
              <div
                className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[min(90vh,calc(100vh-2rem))] overflow-auto relative p-4 sm:p-6"
                role="dialog"
                aria-modal="true"
                aria-labelledby="logout-dialog-title"
              >
                <button
                  onClick={handleLogoutCancel}
                  disabled={isLoggingOut}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 p-1 rounded-md"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-3 mb-4 pr-8">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <LogOut className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 id="logout-dialog-title" className="text-base sm:text-lg font-bold text-gray-900">
                    Confirm Logout
                  </h3>
                </div>
                <p className="text-sm text-gray-700 mb-6">
                  Are you sure you want to logout? You will need to login again to
                  access your account.
                </p>
                <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap gap-3 sm:justify-end">
                  <button
                    onClick={handleLogoutCancel}
                    disabled={isLoggingOut}
                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    No, Cancel
                  </button>
                  <button
                    onClick={handleLogoutConfirm}
                    disabled={isLoggingOut}
                    className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  >
                    {isLoggingOut ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Logging out...
                      </>
                    ) : (
                      "Yes, Logout"
                    )}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        {/* Validation Modal for Navigation Blocking */}
        <ValidationModal
          isOpen={showValidationModal}
          onClose={() => setShowValidationModal(false)}
          message={validationMessage}
        />
      </aside>
    </>
  );
}
